import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { ExternalLink, HelpCircle, SendHorizonal } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { cn } from "@/lib/utils";
import { useFeatureFlags } from "../utils/FeatureFlagDetection";
import { logger } from "@amzn/afxtaco-utilities";
import {
  FileChipList,
  FileUploadButton,
  hasBlockingError,
  SentAttachment,
  useFileUpload,
} from "./fileUpload";

interface ChatInputProps {
  onMessageSend?: (
    message: string,
    features?: {
      usedPrompt?: boolean;
      usedWebSearch?: boolean;
      showThinkingSteps?: boolean;
    },
    // Files uploaded with this message (empty when none). Carried to the
    // backend so the agent knows exactly which files accompany the prompt.
    attachments?: SentAttachment[],
  ) => void;
  onClick?: () => void;
  preselectedAssistant?: {
    name: string;
    description: string;
  };
  defaultMessage?: string;
  placeholder?: string;
  // When true, the input and send button are disabled (e.g. while an execution is in progress).
  disabled?: boolean;
  // Resolves the projectId to upload attachments under, invoked on send.
  resolveUploadProjectId?: () => Promise<string>;
  // Optional callback to open the help panel. When provided, a help icon button
  // is rendered next to the send button.
  onHelpClick?: () => void;
  // Optional content rendered between the textarea and the toolbar row.
  // Used by the homepage to inject the optional context fields.
  slotBelowTextarea?: React.ReactNode;
}

const ChatInput = ({
  onMessageSend,
  onClick,
  defaultMessage,
  placeholder,
  disabled = false,
  resolveUploadProjectId,
  onHelpClick,
  slotBelowTextarea,
}: ChatInputProps) => {
  const resolvedPlaceholder =
    placeholder ??
    (onHelpClick
      ? "What would you like to build? Click the help icon for use cases and tips"
      : "What would you like to build?");
  const [message, setMessage] = useState("");
  const webSearchEnabled = false;
  const [usedPrompt, setUsedPrompt] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Briefly true right after text is inserted from elsewhere, so the input can
  // acknowledge it landed. Nothing is sent — the user still has to press send.
  const [justInserted, setJustInserted] = useState(false);

  // Feature flag on file Upload.
  const { isFileUploadEnabled, isOverviewPageEnabled } = useFeatureFlags([
    "fileUpload",
    "overviewPage",
  ]);

  // Pre-send attachments + upload pipeline (validation and upload live in the hook).
  const { files, isUploading, addFiles, removeFile, clearFiles, uploadAll } =
    useFileUpload();

  // Block sending while a chip is errored or an upload is in flight.
  const sendBlockedByFileError = isFileUploadEnabled && hasBlockingError(files);
  const sendBlockedByUpload = isFileUploadEnabled && isUploading;

  // Adds newly-selected files to the pre-send list (validated inside the hook).
  const handleFilesSelected = useCallback(
    (newFiles: File[]) => {
      addFiles(newFiles);
    },
    [addFiles],
  );

  // Removes a single attachment by id (the chip's ✕ button).
  const handleRemoveFile = useCallback(
    (id: string) => {
      removeFile(id);
    },
    [removeFile],
  );

  // Auto-grow the textarea as the user types
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    // Reset height to auto so scrollHeight recalculates correctly
    textarea.style.height = "auto";
    // Set to scrollHeight, capped by max-h via CSS (max-h-48 = 192px)
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, []);

  // Adjust height whenever message changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [message, adjustTextareaHeight]);

  // Set default message when provided
  useEffect(() => {
    if (defaultMessage && !message) {
      setMessage(defaultMessage);
    }
  }, [defaultMessage, message]);

  // Listen for prompt insertion events from elsewhere in the app (the modal, and
  // the Overview tab's "Ask TACO for this" / "Link a customer" affordances).
  //
  // This only ever POPULATES the input — it never submits. Sending stays a
  // deliberate user action, because a turn is expensive and the text is a
  // starting point the user should be able to edit first.
  //
  // The acknowledgement (ring, focus, scroll-into-view) is gated on
  // `overviewPage`: the prompt modal is the pre-existing publisher of this event
  // and must keep behaving exactly as it does today. Setting the message is not
  // gated — that is the original behaviour.
  useEffect(() => {
    let highlightTimer: ReturnType<typeof setTimeout> | undefined;

    const handleInsertPrompt = (event: CustomEvent) => {
      const { text } = event.detail;
      setMessage(text);
      setUsedPrompt(true);
      if (!isOverviewPageEnabled) return;

      setJustInserted(true);
      // Clear any in-flight timer first: back-to-back inserts would otherwise
      // orphan the earlier one, which still fires and drops the ring while the
      // second insert should still be showing it.
      if (highlightTimer) clearTimeout(highlightTimer);
      highlightTimer = setTimeout(() => setJustInserted(false), 1100);

      // Focus with the caret at the end so the user can keep typing, and bring
      // the input into view in case it was scrolled off. Deferred a frame so it
      // runs after the value has been committed to the DOM.
      requestAnimationFrame(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.focus();
        const end = textarea.value.length;
        textarea.setSelectionRange(end, end);
        textarea.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    };

    window.addEventListener(
      "insertPrompt",
      handleInsertPrompt as EventListener,
    );

    return () => {
      window.removeEventListener(
        "insertPrompt",
        handleInsertPrompt as EventListener,
      );
      if (highlightTimer) clearTimeout(highlightTimer);
    };
  }, [isOverviewPageEnabled]);

  // Listen for clearChatInput events from DataContext
  useEffect(() => {
    const handleClearChatInput = () => {
      setMessage("");
      // Also drop any pre-send attachments so an external clear (e.g. after
      // navigation or a conversation switch) doesn't leave orphaned chips.
      if (isFileUploadEnabled) {
        clearFiles();
      }
    };

    window.addEventListener("clearChatInput", handleClearChatInput);

    return () => {
      window.removeEventListener("clearChatInput", handleClearChatInput);
    };
  }, [isFileUploadEnabled, clearFiles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Guard: require text, not disabled, no errored chips, and not mid-upload.
    if (
      !message.trim() ||
      disabled ||
      sendBlockedByFileError ||
      sendBlockedByUpload
    ) {
      return;
    }

    // Upload attachments to S3 before sending; all-or-nothing. The uploaded
    // attachments (with their final s3Keys) are forwarded to onMessageSend so
    // the backend can associate them with this exact message.
    let attachments: SentAttachment[] = [];
    if (isFileUploadEnabled && files.length > 0) {
      if (!resolveUploadProjectId) {
        // No way to resolve a target project — fail safe and don't send.
        logger.error({
          loggerMsg:
            "[ChatInput] fileUpload enabled with attachments but no resolveUploadProjectId provided; aborting send",
        });
        return;
      }

      let projectId: string;
      try {
        projectId = await resolveUploadProjectId();
      } catch (error) {
        logger.error({
          loggerMsg: "[ChatInput] Failed to resolve upload projectId",
          error,
        });
        return;
      }

      const uploaded = await uploadAll(projectId);
      if (uploaded === null) {
        // A chip is now in ERROR state; abort the send so nothing is lost.
        return;
      }
      attachments = uploaded;
    }

    // Check if message contains "design" or "build" to auto-enable thinking steps
    const lowerMessage = message.toLowerCase();
    const shouldShowThinking =
      lowerMessage.includes("design") || lowerMessage.includes("build");

    onMessageSend?.(
      message,
      {
        usedPrompt,
        usedWebSearch: webSearchEnabled,
        showThinkingSteps: shouldShowThinking,
      },
      attachments,
    );
    setMessage("");
    setUsedPrompt(false); // Reset after sending
    // Files are already persisted to S3; clear the pre-send chips.
    if (isFileUploadEnabled) {
      clearFiles();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();

      handleSubmit(e as React.FormEvent);
    }
  };

  return (
    <div className="!bg-background dark:!bg-[#151a26] p-4 border-t border-border dark:border-transparent">
      <div className="max-w-4xl mx-auto">
        <div className="relative">
          <div
            className={cn(
              "border rounded-lg transition-all duration-300 !border-border !bg-background focus-within:!border-blue-500 focus-within:ring-1 focus-within:ring-blue-500/20 dark:!bg-[#151a26] dark:!border-[#4A9EFF] dark:focus-within:!border-[#4A9EFF] dark:focus-within:ring-2 dark:focus-within:ring-[#4A9EFF]/40 dark:focus-within:shadow-[0_0_15px_rgba(74,158,255,0.3)]",
              // Momentary ring when text arrives from elsewhere, so the user sees
              // where it landed. Rides the existing transition, then fades out.
              justInserted &&
                "!border-blue-500 ring-2 ring-blue-500/40 dark:ring-[#4A9EFF]/50",
            )}
            onClick={onClick}
            onKeyDown={(e) => {
              // Allow Enter from any child (e.g. context fields) to submit,
              // but not from the textarea (which has its own handler).
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                (e.target as HTMLElement).tagName !== "TEXTAREA"
              ) {
                e.preventDefault();
                handleSubmit(e as unknown as React.FormEvent);
              }
            }}
          >
            {/* Pre-send file chips (feature-flagged). */}
            {isFileUploadEnabled && (
              <FileChipList files={files} onRemove={handleRemoveFile} />
            )}

            <div className="relative">
              <form onSubmit={handleSubmit} className="contents">
                <Textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={resolvedPlaceholder}
                  // Stable hook for sibling surfaces that need to hand the user
                  // back to the chat (e.g. the Overview tab's "Start in chat").
                  // The placeholder varies by context, so it can't be a selector.
                  data-taco-chat-input=""
                  rows={1}
                  className="w-full resize-none border-0 px-8 shadow-none !bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 p-4 pt-6 min-h-[3rem] max-h-48 overflow-y-auto text-foreground dark:text-gray-300 placeholder:text-muted-foreground dark:placeholder:text-gray-500"
                  style={{ paddingRight: "32px" }}
                />
              </form>
            </div>

            {/* Slot for injected content between textarea and toolbar */}
            {slotBelowTextarea}

            {/* Bottom row with buttons */}
            <div className="flex items-center justify-between px-3 pb-3">
              {/* Left side: paper-clip attach button + help icon. */}
              <div className="flex items-center gap-2">
                {isFileUploadEnabled && (
                  // Not tied to `disabled` (streaming): the user can stage
                  // attachments for their next message while the agent streams,
                  // mirroring the always-editable textarea.
                  <FileUploadButton onFilesSelected={handleFilesSelected} />
                )}
                {onHelpClick && (
                  <Tooltip>
                    <TooltipTrigger>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={(e) => {
                          e.preventDefault();
                          onHelpClick();
                        }}
                        className="h-9 min-w-9 max-w-9 min-h-9 max-h-9 flex-shrink-0 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200"
                        aria-label="Open help panel"
                      >
                        <HelpCircle className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Use cases and tips</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              {/* Right side send button */}
              <div className="flex items-center gap-2">
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      type="button"
                      variant={
                        message.trim() &&
                        !sendBlockedByFileError &&
                        !sendBlockedByUpload
                          ? "outline"
                          : "outlineDisabled"
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        // Delegate to handleSubmit so send logic lives in one place.
                        handleSubmit(e as React.FormEvent);
                      }}
                      disabled={
                        !message.trim() ||
                        disabled ||
                        sendBlockedByFileError ||
                        sendBlockedByUpload
                      }
                      className={`h-9 min-w-9 max-w-9 min-h-9 max-h-9 flex-shrink-0 transition-all duration-200 ${
                        message.trim() &&
                        !sendBlockedByFileError &&
                        !sendBlockedByUpload
                          ? "text-blue-700 hover:text-blue-800 dark:bg-[#4A9EFF] dark:border-[#4A9EFF] dark:text-white dark:hover:bg-[#3A8EEF] dark:hover:shadow-[0_0_12px_rgba(74,158,255,0.5)]"
                          : "text-muted-foreground cursor-not-allowed dark:bg-gray-600 dark:border-gray-600 dark:text-gray-400"
                      }`}
                    >
                      <SendHorizonal className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {sendBlockedByFileError
                        ? "Remove the file with an error to send"
                        : sendBlockedByUpload
                          ? "Wait for the upload to finish to send"
                          : "Send message"}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-center text-center mt-3 text-xs text-muted-foreground dark:text-gray-500">
          <span>
            Use of this service is subject to the{" "}
            <a
              href="https://aws.amazon.com/machine-learning/responsible-ai/policy/"
              className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:underline inline-flex items-center gap-1"
              target="_blank"
              rel="noreferrer"
            >
              AWS Responsible AI Policy
              <ExternalLink className="h-3 w-3" />
            </a>
            .
          </span>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
